import { createApi } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { Mutex } from "async-mutex";
import { setToken, logout } from "../store/slices/authSlice";
import { env } from "../config/env";
import { mockBaseQuery } from "./mock/mockApiHandler";

const mutex = new Mutex();

// RTK Query axios base query
const baseQuery = async ({ url, method, body, params, contentType = "application/json", withCredentials = false }, api) => {
  const baseURL = env.apiUrl;
  const isFormData = body instanceof FormData;
  const headers = { Accept: "application/json" };

  if (!isFormData) {
    headers["Content-Type"] = contentType;
  }

  try {
    const token = api.getState().auth.token;

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    let data = null;
    if (body) {
      if (isFormData) {
        data = body;
      } else if (contentType === "application/json") {
        // Clone before nullify — mutation bodies may be frozen RTK Query cache objects.
        data = JSON.stringify(nullify(JSON.parse(JSON.stringify(body))));
      } else {
        data = body;
      }
    }

    const result = await axios({
      url: `${baseURL}${url}`,
      method,
      data,
      params,
      headers: {
        ...headers,
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
      ...(withCredentials && { withCredentials: true }),
    });

    return { data: result.data };
  } catch (error) {
    console.error(`API Error (${method} ${url}):`, error.response?.data || error.message);

    return {
      error: {
        status: error.response?.status,
        data: error.response?.data || error.message,
      },
    };
  }
};

const nullify = (obj) => {
  //nullifies the values even if they are deep inside
  if (obj != null && Array.isArray(obj)) {
    for (let i = 0; i < obj.length; i++) {
      if (typeof obj[i] === 'object' && obj[i] !== null && !Array.isArray(obj[i]))
        obj[i] = nullify(obj[i]);
    }
  } else if (obj != null && typeof obj == "object") {
    for (let i = 0, keys = Object.keys(obj); i < keys.length; i++) {
      obj[keys[i]] = nullify(obj[keys[i]]);
    }
  } else if (obj === "" && obj.length === 0 && typeof obj !== "boolean") {
    obj = null;
  }
  return obj;
};

export const baseQueryWithReauth = async (args, api) => {
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock();

  let result = await baseQuery(args, api);
  if (result.error && result.error.status === 401) {
    // checking whether the mutex is locked
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      let refreshSuccess = false;
      try {
        const refreshToken = api.getState().auth.refreshToken;
        if (refreshToken) {
          const refreshResult = await baseQuery(
            {
              url: "/auth/token",
              method: "POST",
              body: new URLSearchParams({
                grant_type: "refresh_token",
                refresh_token: refreshToken,
              }).toString(),
              contentType: "application/x-www-form-urlencoded",
            },
            api
          );

          if (refreshResult.data?.access_token) {
            api.dispatch(
              setToken({
                token: refreshResult.data.access_token,
                refreshToken: refreshResult.data.refresh_token || null,
                expiresAt: refreshResult.data.expires_in
                  ? Date.now() + refreshResult.data.expires_in * 1000
                  : null,
              })
            );
            refreshSuccess = true;
          }
        }
      } catch (e) {
        console.error("Token refresh failed:", e);
      } finally {
        // release must be called once the mutex should be released again.
        release();
      }

      if (refreshSuccess) {
        // retry the initial query with the new token
        result = await baseQuery(args, api);
        if (result.error && result.error.status === 401) {
          api.dispatch(logout());
          window.location.href = "/login";
        }
      } else {
        // refresh failed — log out and redirect to login
        api.dispatch(logout());
        window.location.href = "/login";
      }
    } else {
      // another request is already refreshing — wait for it, then retry
      await mutex.waitForUnlock();
      result = await baseQuery(args, api);
      if (result.error && result.error.status === 401) {
        api.dispatch(logout());
        window.location.href = "/login";
      }
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: async (args, api) => {
    var processedArgs = {};
    if (args.url === undefined) processedArgs.url = args;
    else processedArgs = args;

    // Use mock data when useMockServices is enabled
    if (env.useMockServices) {
      return mockBaseQuery(processedArgs);
    }

    return baseQueryWithReauth(processedArgs, api);
  },
  tagTypes: ["Drugs", 
    "LotNumbers", 
    "Strengths", 
    "User", 
    "Users", 
    "EnvironmentTestType", 
    "EnvironmentTestRecord",
    "CryptoCurrencies",
    "CryptoCurrency",
    "Recommendations",
    "Recommendation",
    "CryptoCurrencyRecommendations",
    "LatestRecommendation",
    "BrokerCompanies",
    "BrokerCompany",
    "BrokerCompanyByNmls",
    "FeatureFlags",
    "LockDesk",
    "Pipeline",
    "Loan",
    "LoanConditions",
    "LoanDocuments",
    "LoanAssets",
    "LoanLiabilities",
    "LoanClosingCosts",
    "VestaContacts",
    "CountyLoanLimits",
    "MarketTrends",
    "BrokerCompensation",
    "AePipeline",
    "RetailPipeline"
  ],
  keepUnusedDataFor: 120,
  refetchOnReconnect: false,
  refetchOnFocus: false,
  endpoints: () => ({})
});

export default baseApi;
